import { generalSummarySpendQueryOptions } from '@/dashboards/ExecutiveSummary/api/generalSummarySpendQueryOptions';
import { useExecutiveSummaryContext } from '@/dashboards/ExecutiveSummary/context';
import {
    calculateSpendTotals,
    Totals,
} from '@/dashboards/ExecutiveSummary/helpers/calculateSpendTotals';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { getCompetitorVariables } from '@/dashboards/utils';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export type { Totals };

export interface GeneralSummarySpendData {
    primary: Totals;
    comparison: Totals;
    /**
     * Totals for the Total Media ROI card only. Identical to `primary` /
     * `comparison` unless the dashboard's `modeledVariablesOnlyInTotalMediaRoi`
     * setting is on, in which case variables with spend but no contribution are
     * excluded. Kept separate so the Total Media Spend and Total Media
     * Contribution cards are unaffected.
     */
    roiPrimary: Totals;
    roiComparison: Totals;
}

export interface UseGeneralSummarySpendQueryResult {
    isLoading: boolean;
    isFetching: boolean;
    data: GeneralSummarySpendData;
}

export const useGeneralSummarySpendQuery =
    (): UseGeneralSummarySpendQueryResult => {
        const {
            project: { roiType, id: projectId },
            dashboard: {
                modeledVariablesOnlyInTotalMediaRoi,
                dashboardVariables,
            },
        } = useDashboardGlobalContext();

        const { primaryRange, comparisonRange } = useExecutiveSummaryContext();

        const competitorVariables = useMemo(
            () => getCompetitorVariables(dashboardVariables),
            [dashboardVariables],
        );

        const primaryQueryResults = useQuery(
            generalSummarySpendQueryOptions({
                projectId,
                roiType,
                startDate: primaryRange?.startDate,
                endDate: primaryRange?.endDate,
                excludeVariables: competitorVariables,
            }),
        );

        const comparisonQueryResults = useQuery(
            generalSummarySpendQueryOptions({
                projectId,
                roiType,
                startDate: comparisonRange?.startDate,
                endDate: comparisonRange?.endDate,
                excludeVariables: competitorVariables,
            }),
        );

        const data = useMemo<GeneralSummarySpendData>(() => {
            const primary = calculateSpendTotals(
                primaryQueryResults.data?.rows,
                modeledVariablesOnlyInTotalMediaRoi,
            );
            const comparison = calculateSpendTotals(
                comparisonQueryResults.data?.rows,
                modeledVariablesOnlyInTotalMediaRoi,
            );

            return {
                primary: primary.totals,
                comparison: comparison.totals,
                roiPrimary: primary.roiTotals,
                roiComparison: comparison.roiTotals,
            };
        }, [
            primaryQueryResults.data?.rows,
            comparisonQueryResults.data?.rows,
            modeledVariablesOnlyInTotalMediaRoi,
        ]);

        return {
            isLoading:
                primaryQueryResults.isLoading ||
                comparisonQueryResults.isLoading,
            isFetching:
                primaryQueryResults.isFetching ||
                comparisonQueryResults.isFetching,
            data,
        };
    };
